import { DEFAULT_REMINDER_DAYS } from '../../../../../platform/components/business/documents/ReminderDayOptions';
import PersonalReminderEmail from '../../../../../platform/components/business/documents/PersonalReminderEmail';
import { DOCUMENT_TYPES as STAFF_DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS as STAFF_DOCUMENT_TYPE_LABELS } from '../../../../../platform/components/business/documents/documentTypes';
/**
 * StaffDocumentsTab.jsx
 *
 * - Upload staff documents
 * - Supports expiration tracking
 * - Metadata saved via GraphQL
 * - Notification-ready
 */

import React, { useContext, useRef, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { uploadToCloudinary } from "../../../../../platform/util/upload/uploadToCloudinary";
import DocumentsTab from "../../../../../platform/components/business/documents/DocumentsTab";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import {
    FeatureAccess,
    getFeatureAccess,
} from "../../../../../platform/util/featureAccess";

/* =========================
   Constants
========================= */


/* =========================
   GraphQL
========================= */
const GET_STAFF_DOCUMENTS = gql`
  query GetStaffDocuments($staffId: ID!) {
    getStaffDocuments(staffId: $staffId) {
      id
      fileName
      fileUrl
      type
      expiresAt
      reminderDays
      createdAt
    }
  }
`;

const GENERATE_UPLOAD_URL = gql`
  mutation GenerateUploadUrl($filename: String!, $contentType: String!) {
    generateUploadUrl(filename: $filename, contentType: $contentType)
  }
`;

const ADD_STAFF_DOCUMENT = gql`
  mutation AddStaffDocument(
    $staffId: ID!
    $type: String!
    $fileUrl: String!
    $fileName: String
    $size: Int
    $expiresAt: DateTime
    $reminderDays: [Int!]
  ) {
    addStaffDocument(
      staffId: $staffId
      type: $type
      fileUrl: $fileUrl
      fileName: $fileName
      size: $size
      expiresAt: $expiresAt
      reminderDays: $reminderDays
    ) {
      id
    }
  }
`;

const DELETE_STAFF_DOCUMENT = gql`
  mutation DeleteStaffDocument($id: ID!) {
    deleteStaffDocument(id: $id)
  }
`;

/* =========================
   Component
========================= */
const UPDATE_DOCUMENT_REMINDERS = gql`mutation UpdateStaffDocumentReminders($id: ID!, $reminderDays: [Int!]!) { updateStaffDocumentReminders(id: $id, reminderDays: $reminderDays) { id reminderDays } }`;

export default function StaffDocumentsTab({ staffId, isDisabled = false }) {
    const { providerUser } = useContext(UserContext);
    const selectedBusiness = useSelectedBusiness();
    const fileInputRef = useRef(null);

    const [reminderDays, setReminderDays] = useState(DEFAULT_REMINDER_DAYS);
    const [updateDocumentReminders] = useMutation(UPDATE_DOCUMENT_REMINDERS);
    const [uploading, setUploading] = useState(false);
    const [docType, setDocType] = useState("OTHER");
    const [customTypeName, setCustomTypeName] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const documentAccess = getFeatureAccess(providerUser, FeatureAccess.DOCUMENT_MANAGEMENT);
    const standardPlanRequired = documentAccess.requiredPlanId;
    const hasStandardDocAccess = documentAccess.hasAccess;
    const currentLoginStaff = selectedBusiness?.businessStaffs?.find(
        (s) => Number(s?.id) === Number(providerUser?.staffId)
    );
    const isAdmin = String(currentLoginStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";
    const docsReadOnly = Boolean(isDisabled || !hasStandardDocAccess || !isAdmin);

    const { data, refetch } = useQuery(GET_STAFF_DOCUMENTS, {
        variables: { staffId },
        skip: !staffId || !isAdmin
    });

    const [generateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
    const [addStaffDocument] = useMutation(ADD_STAFF_DOCUMENT);
    const [deleteStaffDocument] = useMutation(DELETE_STAFF_DOCUMENT);

    const documents = data?.getStaffDocuments || [];

    /* =========================
       Upload Handlers
    ========================= */
    const handleSelectFile = () => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);

        try {
            let fileUrl;

            if (process.env.REACT_APP_ENV === "development") {
                fileUrl = await uploadToCloudinary(file);
            } else {
                const filename = `staff-documents/${staffId}/${Date.now()}-${file.name}`;

                const { data } = await generateUploadUrl({
                    variables: {
                        filename,
                        contentType: file.type
                    }
                });

                const signedUrl = data.generateUploadUrl;

                await fetch(signedUrl, {
                    method: "PUT",
                    headers: { "Content-Type": file.type },
                    body: file
                });

                fileUrl = signedUrl.split("?")[0];
            }

            await addStaffDocument({
                variables: {
                    staffId,
                    type: docType === "OTHER" && customTypeName
                        ? customTypeName
                        : docType,
                    fileUrl,
                    fileName: file.name,
                    size: file.size,
                    expiresAt: expiresAt || null,
                    reminderDays
                }
            });

            await refetch();
        } catch (err) {
            console.error(err);
            alert("Failed to upload document");
        } finally {
            setUploading(false);
            setExpiresAt("");
            setCustomTypeName("");
            e.target.value = "";
        }
    };

    /* =========================
       Delete Handler
    ========================= */
    const handleDelete = async (docId) => {
        if (docsReadOnly) {
            if (!hasStandardDocAccess) setCheckPlanNeeded(true);
            return;
        }
        if (!window.confirm("Delete this document?")) return;
        await deleteStaffDocument({ variables: { id: docId } });
        await refetch();
    };

    /* =========================
       Render
    ========================= */
    return (
        <>
            {isAdmin && selectedBusiness?.businessBasic?.workspaceType === 'PERSONAL' && (
                <PersonalReminderEmail key={selectedBusiness.businessBasic.id} businessId={selectedBusiness.businessBasic.id} />
            )}
            {checkPlanNeeded && (
                <UpgradePlan
                    featureName="Staff document upload and updates"
                    requiredPlanId={standardPlanRequired}
                    onClose={() => setCheckPlanNeeded(false)}
                />
            )}
            {!hasStandardDocAccess && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Standard plan required to upload or delete staff documents.
                </Typography>
            )}
            {hasStandardDocAccess && !isAdmin && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Admin role required to view staff documents.
                </Typography>
            )}
            {isAdmin && (
                <DocumentsTab
                title="Staff Documents"
                documentTypes={STAFF_DOCUMENT_TYPES}
                documents={documents}
                docType={docType}
                onChangeDocType={setDocType}
                customTypeName={customTypeName}
                onChangeCustomTypeName={setCustomTypeName}
                expiresAt={expiresAt}
                onChangeExpiresAt={setExpiresAt}
                reminderDays={reminderDays}
                onChangeReminderDays={setReminderDays}
                onUpdateReminders={async (id, days) => {
                    if (!isAdmin) throw new Error('You do not have permission to change document reminders.');
                    await updateDocumentReminders({ variables: { id, reminderDays: days } });
                    await refetch();
                }}
                uploading={uploading}
                onSelectFile={handleSelectFile}
                onFileSelected={handleFileSelected}
                onDelete={handleDelete}
                fileInputRef={fileInputRef}
                groupByType
                typeLabelMap={STAFF_DOCUMENT_TYPE_LABELS}
                isDisabled={docsReadOnly}
                remindersDisabled={!isAdmin}
            />
            )}
        </>
    );
}
